import { computed, DestroyRef, inject, type Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  isTimesheetRow,
  type CategorizedTimesheetGroups,
} from '@app/+authenticated/+timesheet/list/categorized-timesheet-row';
import {
  TIMESHEET_CATEGORIES,
  type TimesheetCategory,
} from '@app/+authenticated/+timesheet/shared/categorization/timesheet-category';
import {
  getOpenClosedForPeriodAndDepartments,
  type OpenCloseData,
  type OpenCloseStatus,
} from '@app/+modals/timesheet/open-close-days/open-close-days.helper';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { ContractService } from '@app/reducers/orm/contract/contract.service';
import { Store } from '@ngrx/store';
import { _, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { hasPermissionForAllDepartments } from '@sb/shared/permission/util';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import groupBy from 'lodash-es/groupBy';
import { catchError, EMPTY, filter, switchMap, take, tap } from 'rxjs';

import { PayrollBalancesService } from './payroll-balances.service';
import { subTextKeyFor, type PayrollSubTextKeys } from './payroll-progress';
import { usePayrollTimesheets } from './payroll-timesheet-load';
import { type PayrollRunScope, type PayrollStepSources } from './payroll.model';
import { PayrollAbsencesStore } from './steps/absences/payroll-absences.store';
import { PayrollMinijobService } from './steps/minijob/payroll-minijob.service';

const RESOLVED_CATEGORIES: TimesheetCategory[] = ['APPROVED', 'DECLINED'];

/**
 * Outstanding timesheets for the payroll step: rows still needing a manager decision —
 * `REQUIRES_ATTENTION`, `IN_PROGRESS`, `READY_FOR_APPROVAL` — count however the list categorizes
 * their severity. `APPROVED` and `DECLINED` are both terminal decisions the manager already made,
 * so neither counts, even though the list still shows a `Declined` section for visibility. Rows on
 * a locked day are excluded too — they stay read-only and can no longer be acted on.
 */
export function countOutstandingTimesheets(groups: CategorizedTimesheetGroups): number {
  return TIMESHEET_CATEGORIES.filter((category) => !RESOLVED_CATEGORIES.includes(category)).reduce(
    (count, category) => count + groups[category].filter(isTimesheetRow).filter((row) => !row.timesheet.closed).length,
    0,
  );
}

/** How much of a period still needs locking, and how uniformly its days are still open. */
export interface PayrollOpenDays {
  /** Dates carrying at least one open department. Never exceeds the days in the range. */
  count: number;
  /** At least one counted date mixes still-open departments with already-locked ones. */
  partiallyOpen: boolean;
}

const OPEN_STATUSES: OpenCloseStatus[] = ['open', 'pending'];

/**
 * Summarises the days that still need locking. A date counts once however many of its departments
 * are open, so the count stays a number the manager can hold against the calendar period they
 * picked — a per-department tally reads as a multiple of the days in the range and describes
 * nothing they can see.
 *
 * 'open' (unlocked, nothing pending) and 'pending' (unlocked, still has pending timesheets) are
 * both open. Counting both also makes the 'open' vs 'pending' split irrelevant to the *count*,
 * which is convenient because that split depends on pending-timesheet data the grid loads
 * asynchronously.
 *
 * `canEdit` deliberately does **not** filter here. A day the manager has no permission to close
 * still counts as outstanding, so a manager with rights on only some departments sees a step that
 * never reaches zero. That is intended (SHB-24153 AC 7): the panel reports that work remains and
 * someone else has to do it. It blocks nothing, since the report is not gated on completion.
 *
 * 'disabled' cells are the opposite case and drop out before anything is classified: they cannot
 * be closed by anyone, so they neither make a date count nor make one read as partially open, and
 * a date with nothing but disabled departments leaves the summary entirely. Nothing mixes disabled
 * with open in practice — the status follows the date alone — so the fully-vs-partially split turns
 * on already-locked departments only.
 *
 * `partiallyOpen` describes the whole period rather than any one date: a single date with some
 * departments already locked sets it, because the panel has one sentence to spend on the count.
 */
export function summarizeOpenDays(days: OpenCloseData[]): PayrollOpenDays {
  const lockableByDate = groupBy(
    days.filter((departmentDay) => departmentDay.status !== 'disabled'),
    (departmentDay) => departmentDay.date,
  );

  const openDates = Object.values(lockableByDate)
    .map((departmentDays) => ({
      open: departmentDays.filter((departmentDay) => OPEN_STATUSES.includes(departmentDay.status)).length,
      lockable: departmentDays.length,
    }))
    .filter((tally) => tally.open > 0);

  return {
    count: openDates.length,
    partiallyOpen: openDates.some((tally) => tally.open < tally.lockable),
  };
}

/**
 * Warms the store the `lock`, `balances` and `timesheets` sources read — the one place that
 * answers "what does a payroll run load for its scope". (The absence step is the exception, and
 * a deliberate one: `PayrollAbsencesStore` is a signal store that owns its own `rxMethod` load
 * and request status, so it has no store to warm from here.) Must be called from an injection
 * context, and only needs calling once per run host — it follows the scope for the rest of the
 * flow's life, and its `takeUntilDestroyed` ends every request when the run host goes away.
 *
 * The progress panel shows every count from step one, long before the manager opens the step whose
 * own screen would load that range. Without this, a fully closed period reads as fully open in the
 * panel until the lock step is visited, and no employee looks like a contract holder at all —
 * nothing else in the app loads contracts, since startup covers employees and contract *types*
 * only. Everything is loaded for the run's own departments: the lock count reads the timesheets
 * and day logs `usePayrollTimesheets` loads, filtered the same way the lock selector is.
 *
 * The lock count is only *eventually* right: `PayrollStepSource.outstanding` is a plain
 * `Signal<number>` with no "not known yet" state, so every day reads as open until the response
 * lands. The direction is safe — it over-reports work rather than announcing a false all-clear —
 * and modelling the in-flight state belongs with the request-status work the other sources need
 * anyway.
 *
 * Contracts and timesheets cannot lean on that, because a cold store makes employees read as
 * *absent*, and timesheets as *nothing outstanding*, rather than as work not yet known — a false
 * all-clear either way. So each reports its request's progress back to the service that owns the
 * step's state (`PayrollBalancesService`, `PayrollRunService`), which withholds the source until
 * there is an answer.
 */
export function usePayrollScopeData(scope: Signal<PayrollRunScope | null>): void {
  const contractService = inject(ContractService);
  const balances = inject(PayrollBalancesService);
  const destroyRef = inject(DestroyRef);

  toObservable(scope)
    .pipe(
      filter((currentScope): currentScope is PayrollRunScope => !!currentScope),
      tap(() => balances.onContractsRequested()),
      switchMap((currentScope) =>
        contractService
          .load({
            minDate: toISODateString(currentScope.range.start),
            maxDate: toISODateString(currentScope.range.end),
          })
          .pipe(
            take(1),
            tap(() => balances.onContractsSettled(true)),
            // Settled but not loaded: the step stops waiting for an employee set that is not
            // coming, while its count stays unresolved rather than resolving on an empty store. A
            // failure also has to stay inside the inner observable: reaching the outer stream would
            // end the subscription, so no later scope change would ever reload.
            catchError(() => {
              balances.onContractsSettled(false);
              return EMPTY;
            }),
          ),
      ),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe();

  // The run's timesheets and day logs, warmed for the same reason as the contracts: the progress
  // panel's timesheet and lock counts read them from step one, long before the manager opens
  // either step. Owns the priority-endpoint categorization too, hence its own file.
  usePayrollTimesheets(scope);
}

/**
 * A step's sentence about its own backlog, branched on the count so zero states the settled
 * position and one reads in the singular. The count is passed to every branch, including the
 * settled one, which simply has no `[count]` to resolve.
 */
function countSubText(translate: TranslateService, count: Signal<number>, keys: PayrollSubTextKeys): Signal<string> {
  return computed(() => {
    const outstanding = count();

    return translate.instant(subTextKeyFor(outstanding, keys), { count: outstanding });
  });
}

const NO_OPEN_DAYS = _('No open days');

/** Every counted date is open across all of the run's departments. */
const OPEN_DAY_KEYS: PayrollSubTextKeys = {
  zero: NO_OPEN_DAYS,
  one: _('[count] open day'),
  many: _('[count] open days'),
};

/** At least one counted date has some of the run's departments already locked. */
const PARTIALLY_OPEN_DAY_KEYS: PayrollSubTextKeys = {
  zero: NO_OPEN_DAYS,
  one: _('[count] partially open day'),
  many: _('[count] partially open days'),
};

/**
 * The lock step's sentence, which — unlike every other step's — turns on more than its count: the
 * manager needs to tell "nothing is locked yet" from "I am nearly done". Both key sets name the
 * same settled sentence, and only `OPEN_DAY_KEYS` can ever reach it: there is nothing to be
 * partially open about where no date counts.
 */
function openDaysSubText(translate: TranslateService, openDays: Signal<PayrollOpenDays>): Signal<string> {
  return computed(() => {
    const { count, partiallyOpen } = openDays();
    const keys = partiallyOpen ? PARTIALLY_OPEN_DAY_KEYS : OPEN_DAY_KEYS;

    return translate.instant(subTextKeyFor(count, keys), { count });
  });
}

/**
 * Step sources for the run's scope — every step now has one.
 * `getOpenClosedForPeriodAndDepartments` takes an explicit department set and range,
 * `PayrollBalancesService` and `PayrollMinijobService` ask for the period's employees and payouts
 * themselves, `PayrollAbsencesStore` derives from the run's own scope directly, and
 * `timesheetGroups` is passed in already scoped. None of them read the global page-filter state.
 *
 * `lock` reads the same selector the lock step's grid renders from, so the count in the progress
 * panel and the cells in the grid cannot disagree — they are two projections of one derivation.
 *
 * `store.selectSignal` is called inside the outer `computed` deliberately: the selectors it wraps
 * are parameterised by the run's date range and departments, so a new scope needs new selector
 * instances. This is safe to do here because `Store#selectSignal` is itself just
 * `computed(() => selector(state()))` (see `@ngrx/store`) — no subscription is created, so there
 * is nothing to leak when the outer `computed` discards previous instances on the next scope change.
 *
 * `timesheetGroups` is `PayrollRunService.timesheetGroups`, not built here — the run service also
 * owns the load that populates it, so deriving from the same signal (rather than a second
 * `getCategorizedTimesheetGroupsForRange` instance) is what makes the count reflect loaded data
 * instead of racing a load this function has no way to trigger.
 *
 * The `timesheets` source is only wired when the manager can view every department in the run's
 * scope: the range selectors already drop a row whose department the manager cannot view, which
 * is indistinguishable, at the count, from that department genuinely having nothing outstanding.
 * Omitting the source for an unmeasurable scope leaves the step 'outstanding' — the same safe
 * fallback a step with no source at all already gets — rather than reporting a false all-clear for
 * departments the manager cannot see into. Gated on `'View all timesheets'` alone — `'View own
 * timesheet'` resolves `true` for a manager who can only see their own rows in a department, which
 * would pass this gate while the range selectors still drop every other employee's row there,
 * reintroducing the exact false all-clear the gate exists to prevent.
 *
 * `timesheetsUnavailable` — from `PayrollRunService.timesheetsUnavailable` — folds in "still
 * loading" and "load failed": both leave `timesheetGroups` unable to distinguish "genuinely
 * nothing outstanding" from "not known yet", so the source is omitted for the same reason a
 * permission gap omits it, rather than briefly or permanently reporting a false all-clear.
 */
export function usePayrollStepSources(
  scope: Signal<PayrollRunScope | null>,
  timesheetGroups: Signal<CategorizedTimesheetGroups>,
  timesheetsUnavailable: Signal<boolean>,
): Signal<PayrollStepSources> {
  const store = inject<Store<AppState>>(Store);
  const translate = inject(TranslateService);
  // Owns its own request rather than deriving from the store, so its signals are read straight
  // through — unlike `lock`, there is no selector to re-instantiate per scope here.
  const balances = inject(PayrollBalancesService);
  const absencesStore = inject(PayrollAbsencesStore);
  const minijob = inject(PayrollMinijobService);
  const permissionState = store.selectSignal(getPermissionState);

  return computed<PayrollStepSources>(() => {
    const currentScope = scope();

    if (!currentScope) {
      return {};
    }

    const startDate = toISODateString(currentScope.range.start);
    const endDate = toISODateString(currentScope.range.end);

    const openCloseDepDays = store.selectSignal(
      getOpenClosedForPeriodAndDepartments(currentScope.departmentIds, startDate, endDate),
    );

    const openDays = computed(() =>
      summarizeOpenDays(Object.values(openCloseDepDays()).flatMap((daysByDate) => Object.values(daysByDate))),
    );
    const outstanding = computed(() => openDays().count);
    const pendingAbsences = absencesStore.pendingCount;

    const timesheetsOutstanding = computed(() => countOutstandingTimesheets(timesheetGroups()));

    // The length check is not redundant: `hasPermissionForAllDepartments` treats an empty
    // `departments` array as "unspecified" and falls back to the global `selectedDepartments`,
    // and if that is empty too its `every()` over an empty list answers `true` — wiring the
    // source with no permission actually verified. No caller reaches it today: the header does
    // not reject an empty department pick, it discards the run, so the scope goes null and the
    // `!currentScope` return above catches it first. This gate is the last thing standing between
    // a scope the manager cannot fully see and a false all-clear, so it does not lean on that.
    const canViewEveryRunDepartment =
      currentScope.departmentIds.length > 0 &&
      hasPermissionForAllDepartments(
        {
          permissions: ['View all timesheets'],
          userId: 'me',
          departments: currentScope.departmentIds,
        },
        permissionState(),
      );

    return {
      balances: {
        outstanding: balances.outstanding,
        subText: balances.subText,
      },
      // Deliberately absent until the first load resolves. `deriveStepViews` treats an
      // `outstanding()` of 0 as 'complete', and an unloaded store legitimately reports 0 — which
      // would mark the step done and let the page's auto-advance carry the manager past it before
      // they saw a single request. A missing source reads as 'outstanding' instead, so the step can
      // only resolve itself against real data. The narrow fix on purpose: modelling "not known yet"
      // properly means changing `outstanding`, `deriveStepViews`, `countResolved` and the progress
      // denominator, which is the follow-up this file's `lock` comment already describes.
      ...(absencesStore.hasLoaded()
        ? {
            absences: {
              outstanding: pendingAbsences,
              subText: countSubText(translate, pendingAbsences, {
                zero: _('No pending requests'),
                one: _('[count] pending request'),
                many: _('[count] pending requests'),
              }),
            },
          }
        : {}),
      // Withheld until every month in scope has answered, for the same reason as `absences`: an
      // unloaded answer reads as zero, and zero reads as done.
      ...(minijob.hasLoaded()
        ? {
            minijob: {
              outstanding: minijob.outstanding,
              subText: minijob.subText,
            },
          }
        : {}),
      lock: {
        outstanding,
        subText: openDaysSubText(translate, openDays),
      },
      ...(canViewEveryRunDepartment &&
        !timesheetsUnavailable() && {
          timesheets: {
            outstanding: timesheetsOutstanding,
            subText: countSubText(translate, timesheetsOutstanding, {
              zero: _('No pending timesheets'),
              one: _('[count] pending timesheet'),
              many: _('[count] pending timesheets'),
            }),
          },
        }),
    };
  });
}
